import { ReactElement, useMemo } from 'react'
import styled from 'styled-components'
import { I18n, Translate } from 'common'
import { StockOptions } from 'results/constants'
import { IdValuePair } from 'interfaces'
import { RadioGroup } from '../../../ui-components/forms/RadioGroup'
import { ListItem } from '../interfaces'
import { stockOptionI18nKey } from '../stockFilterLabels'

interface StockFilterSectionProps {
  options: ListItem[]
  selectedValue: string
  onSelect: (option: ListItem) => void
  hideLabel?: boolean
}

export const StockFilterSection = ({
  options,
  selectedValue,
  onSelect,
  hideLabel = false,
}: StockFilterSectionProps): ReactElement | null => {
  const stockItems = options.map((option) => option.value as StockOptions)

  const radioChoices: Array<IdValuePair<string>> = useMemo(
    () =>
      options.map((option) => ({
        id: option.value.toString(),
        value: Translate(stockOptionI18nKey(option.value as StockOptions)),
      })),
    [options]
  )

  const handleRadioChange = (choice: IdValuePair<string>): void => {
    const selected = options.find(
      (option) => option.value.toString() === choice.id
    )
    if (selected) {
      onSelect(selected)
    }
  }

  if (stockItems.length < 1) {
    return null
  }

  const singleOption = stockItems.length === 1 ? stockItems[0] : undefined

  return (
    <Section aria-labelledby={hideLabel ? undefined : 'stock-filter-label'}>
      {!hideLabel && (
        <StockLabel id="stock-filter-label">
          <I18n name="stock" />
        </StockLabel>
      )}
      {singleOption ? (
        <SingleOptionText>
          <I18n name="showing" />{' '}
          <I18n name={stockOptionI18nKey(singleOption)} />.
        </SingleOptionText>
      ) : (
        <StockRadioList role="radiogroup" aria-labelledby="stock-filter-label">
          <RadioGroup
            key={selectedValue}
            id="stock-filter"
            name="stock-filter"
            options={radioChoices}
            initialValue={selectedValue}
            onChange={handleRadioChange}
            compact
          />
        </StockRadioList>
      )}
    </Section>
  )
}

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StockLabel = styled.div`
  font-family: Roboto, serif;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  color: ${(p) => p.theme.colors.text};
`

const SingleOptionText = styled.div`
  font-family: Roboto, serif;
  font-size: 16px;
  line-height: 24px;
  color: ${(p) => p.theme.colors.text};
`

const StockRadioList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
